<template>
	<div
		v-if="quest"
		class="quest-box-rewards"
		:class="{ 'quest-box-rewards--completed': questCompleted }"
	>
		<div class="quest-box-rewards__content">
			<BidirectionalTextLabel
				:text="translate('ui.rewards')"
				:theme="LabelThemes.GREEN"
				class="rewards-label"
			/>
			<div
				class="quest-box-reward"
				v-for="(reward, i) in quest.reward.content"
				:key="i"
			>
				<div class="quest-box-reward__config">
					<Tooltip
						v-if="
							quest.reward.type ===
							QuestRewardTypes.GET_BUILDING_TO_STOCK
						"
						:key="questReward(reward)"
						:placement="Placements.TOP"
					>
						<template #element>
							<AppItemAmount
								:amount="getAmount(reward.amount)"
								:frameName="questReward(reward)"
								:iconSize="16"
							/>
						</template>
						<template #tooltip>
							<span>
								{{ translate(`${questReward(reward)}.name`) }}
							</span>
						</template>
					</Tooltip>

					<AppPrice
						v-else
						:amount="getAmount(reward.amount)"
						:currency="
							resourceTypeToCurrencyType(
								reward.reward_resource as ResourceType,
							)
						"
						:resourceCheck="false"
						:resourceChangeHighlight="
							ResourceChangeHighlight.POSITIVE
						"
						:iconSize="16"
					/>
					<span
						v-if="quest.reward.type === 'RESOURCE_INCOME'"
						class="resource_income"
					>
						{{ `/ ${GAME_TICKER_INTERVAL / 1_000}s` }}
					</span>
				</div>
			</div>
		</div>
		<RectangleBoxContainer />
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import BidirectionalTextLabel from "@/components/BidirectionalTextLabel.vue";
import RectangleBoxContainer from "@/components/SVG/RectangleBoxContainer.vue";
import { GAME_TICKER_INTERVAL } from "@/config/constants";
import type { FrameName } from "@/game/entities/config";
import { QuestRewardTypes } from "@/game/entities/config/quests/quest_reward_types";
import type { ResourceType } from "@/game/entities/config/resources";
import type {
	ExplorationReward,
	FrameReward,
	QuestActive,
	ResourceReward,
} from "@/types/quest";
import { LabelThemes, Placements, ResourceChangeHighlight } from "@/types/ui";
import { resourceTypeToCurrencyType } from "@/utils/formatPrice";
import { getAmount } from "@/utils/number";
import { translate } from "@/utils/translate";
import AppItemAmount from "../AppItemAmount.vue";
import Tooltip from "../Tooltip.vue";

interface Props {
	quest: QuestActive;
	questCompleted: boolean;
}

const { quest = null, questCompleted = false } = defineProps<Props>();

const questReward = (
	reward: ResourceReward | FrameReward | ExplorationReward,
): FrameName => {
	return reward.reward_resource as FrameName;
};
</script>

<style lang="scss" scoped>
.quest-box-rewards {
	width: 100%;
	height: 3.813rem;
	@include flex-row-center;
	position: relative;
	margin-top: 1rem;
	background-image: linear-gradient(to top, rgb(37, 38, 42), rgb(66, 67, 74));

	.quest-box-reward__config {
		display: flex;
		align-items: center;
		gap: 0.2rem;
	}

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		mix-blend-mode: color-burn;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat center;
		background-size: 40%;
		width: 100%;
		height: 100%;
		content: "";
	}

	&__content {
		@include flex-col-center;
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}

	&--completed {
		margin-bottom: 0;
		box-shadow: inset 0 0 2.375rem 0rem rgba($color: #2bad50, $alpha: 0.7);
	}

	.rewards-label {
		position: absolute;
		top: -1rem;
		z-index: 1;
		width: auto;
		height: 2rem;
	}

	.resource_income {
		color: #f2dcb5;
		line-height: 1rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
	}

	:deep(.bidirectional-text-label) {
		filter: drop-shadow(0 0 0.25rem rgba(0, 0, 0, 0.6));
	}

	:deep(.price__value) {
		color: #f2dcb5 !important;
	}
}
</style>
